import React, { useEffect, useRef } from 'react';
import { Car, Clock, Loader, MapPin } from 'lucide-react';
import { gsap } from 'gsap';
import type { TravelInfo as TravelInfoType } from '../services/googleMapsService';

interface TravelInfoProps {
  travelInfo: TravelInfoType | null;
  isLoading: boolean;
  stopsCount?: number;
  className?: string;
}

const TravelInfo: React.FC<TravelInfoProps> = ({ 
  travelInfo, 
  isLoading, 
  stopsCount = 0,
  className = '' 
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const distanceRef = useRef<HTMLDivElement>(null);
  const timeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (travelInfo && containerRef.current) {
      // Animate container appearance
      gsap.fromTo(containerRef.current,
        { opacity: 0, y: 20, scale: 0.95 },
        { 
          opacity: 1, 
          y: 0, 
          scale: 1, 
          duration: 0.5, 
          ease: "back.out(1.7)" 
        }
      );

      // Animate individual info items
      gsap.fromTo([distanceRef.current, timeRef.current],
        { opacity: 0, x: -20 },
        { 
          opacity: 1, 
          x: 0, 
          duration: 0.4, 
          stagger: 0.1, 
          delay: 0.2,
          ease: "power2.out" 
        }
      );
    }
  }, [travelInfo]);

  if (isLoading) {
    return (
      <div className={`bg-blue-50 border-2 border-blue-200 rounded-xl p-4 ${className}`}>
        <div className="flex items-center justify-center space-x-3">
          <Loader className="w-5 h-5 text-blue-600 animate-spin" />
          <span className="text-blue-700 text-sm font-medium">Calculating route…</span>
        </div>
      </div>
    );
  }

  if (!travelInfo) {
    return (
      <div className={`bg-gray-50 border-2 border-gray-200 rounded-xl p-4 ${className}`}>
        <div className="flex items-center justify-center space-x-3 text-gray-500">
          <MapPin className="w-5 h-5" />
          <span className="font-medium text-sm">Add pickup, stops, and drop-off to see distance and time.</span>
        </div>
      </div>
    );
  }

  if (travelInfo.status === 'ERROR') {
    return (
      <div className={`bg-red-50 border-2 border-red-200 rounded-xl p-4 ${className}`}>
        <div className="flex items-center justify-center space-x-3 text-red-600">
          <MapPin className="w-5 h-5" />
          <span className="font-medium">Unable to calculate route</span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`rounded-xl border border-gray-200 bg-gray-50 p-4 ${className}`}
    >
      <div className="grid grid-cols-2 gap-4">
        <div 
          ref={distanceRef}
          className="flex items-center space-x-3"
        >
          <div className="flex-shrink-0">
            <div className="w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center">
              <Car className="w-4 h-4 text-gray-600" />
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Distance</p>
            <p className="text-base font-semibold text-gray-900">{travelInfo.distance.text}</p>
          </div>
        </div>

        <div 
          ref={timeRef}
          className="flex items-center space-x-3"
        >
          <div className="flex-shrink-0">
            <div className="w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center">
              <Clock className="w-4 h-4 text-gray-600" />
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Est. time</p>
            <p className="text-base font-semibold text-gray-900">{travelInfo.duration.text}</p>
          </div>
        </div>
      </div>

      {stopsCount > 0 ? (
        <p className="text-center text-xs text-gray-500 mt-3 pt-3 border-t border-gray-200">
          {stopsCount} extra stop{stopsCount > 1 ? 's' : ''}
        </p>
      ) : null}
    </div>
  );
};

export default TravelInfo;